import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { Dialog } from "primereact/dialog";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../models/components/field-message.model";
import { HelperService } from "../../services/helpers/helper.service";
import UiLink from "../link/Link.component";
import { TableTemplateKannactId } from "../../models/components/table/table-template.model";
import { Formatter } from "../../services/formatter/formatter.service";
import { EndpointsService } from "../../services/endpoints/endpoints.service";
import UiFieldMessage from "../field-message/FieldMessage.component";
import FormSection from "../form-section/FormSection.component";
import { ModalTwilioAudioProps } from "../../models/modal-twilio-audio.model";
import { Transcript, TranscriptSentence } from "../../models/entities/transcript.model";
import UiButton from "../button/Button";
import { BiCopy } from "react-icons/bi";
import { FaCheck } from "react-icons/fa";

const ModalTwilioAudio = (props: ModalTwilioAudioProps): JSX.Element => {
	const { t } = useTranslation("common");

	useEffect(() => {
		if (!props.mediaUrl) {
			getRecordingData();
		}
		if (transcripts.length === 0) {
			getTranscriptData();
		}
	}, [props.callId]);

	// Messages
	const messages = {
		loading: "UI_COMPONENTS.TABLE.TEMPLATES.TWILIO_AUDIO.MESSAGE_LOADING",
		empty: "UI_COMPONENTS.TABLE.TEMPLATES.TWILIO_AUDIO.MESSAGE_EMPTY",
		error: "UI_COMPONENTS.TABLE.TEMPLATES.TWILIO_AUDIO.MESSAGE_ERROR",
	};
	const [message, setMessage] = useState<UiFieldMessageProps>({
		severity: FieldMessageSeverity.LOADING,
		label: messages.loading,
	});

	const [copiedTranscriptIndex, setCopiedTranscriptIndex] = useState<number | null>(null);
	const [currentTime, setCurrentTime] = useState(0);

	const [audioDurations, setAudioDurations] = useState<{ [key: string]: number }>({});

	const handleTimeUpdate = (target: HTMLAudioElement) => {
		const offsetToAdd = calculateOffsetFromUrl(target.src);
		setCurrentTime(target.currentTime + offsetToAdd);
	};

	const calculateOffsetFromUrl = (src: string) => {
		let acumulatedOffset = 0;
		for (const element of props?.mediaUrl || []) {
			if (element === src) {
				break;
			}
			acumulatedOffset += audioDurations[element];
		}
		return acumulatedOffset;
	};

	const [transcripts, setTranscripts] = useState<Transcript[]>([]);

	const getTranscriptData = async (): Promise<void> => {
		// Loading
		setMessage({ severity: FieldMessageSeverity.LOADING, label: messages.loading });

		// Get transcripts
		await EndpointsService.twilio
			.getTranscript(props.callId as string)
			.then((response: Transcript[]) => {
				setTranscripts(response);
			})
			.catch((error: AxiosError) => console.log(error));
	};

	/**
	 * Fired when an audio element's metadata (including duration) has loaded.
	 * @param target The HTMLAudioElement that fired the event.
	 */
	const handleMetadataLoaded = (target: HTMLAudioElement) => {
		if (target.duration && !isNaN(target.duration)) {
			setAudioDurations((prevDurations) => ({
				...prevDurations,
				[target.src]: target.duration,
			}));
		}
	};

	const handleCopyContentToClipboard = (transcript: Transcript, index: number) => {
		navigator.clipboard.writeText(
			transcript.transcript
				.map(
					(t) =>
						t.startTime +
						" - " +
						t.endTime +
						" " +
						(t.isAgent ? "Agent: " : "Customer: ") +
						t.transcript
				)
				.join("\n")
		);
		setCopiedTranscriptIndex(index);

		setTimeout(() => {
			setCopiedTranscriptIndex(null);
		}, 3000);
	};

	/**
	 * Endpoint: get call recording
	 * @description Call the endpoint to retreive the recording.json file for this specific call
	 */
	const getRecordingData = async (): Promise<void> => {
		// Loading
		setMessage({ severity: FieldMessageSeverity.LOADING, label: messages.loading });

		// Get recording
		await EndpointsService.twilio
			.getRecording(props.callId as string)
			.then((response: string[]) => {
				if (response) {
					props.onUpdate({ ...props, mediaUrl: response });
				} else {
					// No data
					props.onUpdate({ ...props, mediaUrl: null });
					setMessage({ severity: FieldMessageSeverity.INFO, label: messages.empty });
				}
			})
			.catch(() =>
				setMessage({ severity: FieldMessageSeverity.DANGER, label: messages.error })
			);
	};

	// Helper function to determine if a message is active
	const isMessageActive = (startTime: number, endTime: number) => {
		return currentTime >= startTime && currentTime < endTime;
	};

	return (
		<Dialog
			header={t("UI_COMPONENTS.TABLE.TEMPLATES.TWILIO_AUDIO.TITLE")}
			visible={props.isVisible}
			breakpoints={{ "960px": "50vw", "640px": "100vw" }}
			style={{ width: "40vw" }}
			className="modal-twilio-audio"
			modal={false}
			draggable={true}
			resizable={false}
			focusOnShow={false}
			onHide={props.onClose}
		>
			<FormSection className="mb-3">
				{/* Kannact ID */}
				{HelperService.isKannactId(props.kannactId as string) ? (
					<UiLink {...new TableTemplateKannactId(props.kannactId as string).props} />
				) : (
					<span>{props.kannactId}</span>
				)}

				{/* Call date/time */}
				{props.callDate && (
					<span className="mx-3">
						{Formatter.date(props.callDate, {
							day: "2-digit",
							month: "2-digit",
							year: "2-digit",
							hour: "2-digit",
							minute: "2-digit",
						})}
					</span>
				)}
			</FormSection>
			{/* There is an audio file */} 
			{props?.mediaUrl?.map((url, idx) => (
				<audio
					key={idx}
					style={{ width: "100%" }}
					controls
					src={url}
					onLoadedMetadata={(event) => handleMetadataLoaded(event.currentTarget)}
					onTimeUpdate={(event) => handleTimeUpdate(event.currentTarget)}
				>
					<track
						kind="captions"
						src=""
						label="No captions"
					/>
				</audio>
			))}
			{/* There isn't an audio file --> show message (empty/error) */}
			{!props?.mediaUrl && (
				<UiFieldMessage
					severity={message.severity}
					label={message.label}
				></UiFieldMessage>
			)}
			{transcripts.length > 0 && (
				<FormSection className="mb-3">
					<div
						className="modal-twilio-audio__transcripts-container"
						style={{ display: "flex", gap: "20px" }}
					>
						{transcripts.map((transcript, transcriptIndex) => (
							<div
								key={transcriptIndex}
								className="modal-twilio-audio__transcript-column"
								style={{ flex: 1 }}
							>
								<div className="d-flex align-items-center">
									<h4 className="modal-twilio-audio__transcript-title">
										Transcript {transcript.provider} - {transcript.model}
									</h4>
									{copiedTranscriptIndex !== transcriptIndex ? (
										<UiButton
											className="p-button-outlined p-button-xs"
											style={{ marginLeft: "10px" }}
											label="UI_COMPONENTS.BUTTONS.COPY"
											icon={<BiCopy />}
											onClick={() =>
												handleCopyContentToClipboard(
													transcript,
													transcriptIndex
												)
											}
										/>
									) : (
										<UiButton
											className="p-button-outlined p-button-xs"
											label="UI_COMPONENTS.BUTTONS.COPIED"
											style={{ pointerEvents: "none", marginLeft: "10px" }}
											icon={<FaCheck />}
										/>
									)}
								</div>
								{transcript?.transcript?.map(
									(message: TranscriptSentence, index: number) => {
										const active = isMessageActive(
											message.startTime,
											message.endTime
										);
										return (
											<div
												key={index}
												className={`modal-twilio-audio__transcript-message ${active ? "modal-twilio-audio__transcript-message--active" : ""}`}
											>
												<div className="modal-twilio-audio__speaker-info">
													<span className="modal-twilio-audio__speaker-info__font-medium">
														{message.isAgent ? "Agent:" : "Customer:"}
													</span>
													<span className="modal-twilio-audio__speaker-info__timestamp">
														{message.startTime} - {message.endTime}
													</span>
												</div>
												<div className="mt-1">{message.transcript}</div>
											</div>
										);
									}
								)}
							</div>
						))}
					</div>
				</FormSection>
			)}
		</Dialog>
	);
};
export default ModalTwilioAudio;
